import React, { useState, useEffect, useRef } from 'react';
import { WithOverrides } from '../../../components';
import style from './new.module.scss';
import { Action, Option, MessageResp } from '../types';
import { useMutate, useAppAndBranchId, useQuery } from '../../../hooks';
import { api } from '../api';
import { LoadingIcon } from '../../../icons/LoadingIcon';
import { useSelector } from 'react-redux';
import { AppState, RootState } from '../../../../../../../stateManager/store';
import { useHistory, useParams } from 'react-router-dom';
import { Sidebar } from './components';
import { useToast } from '../../../../../../../helpers/commonFunction';
import { MessageRespWrapper } from './components/MessageRespWrapper';
import { SetState } from '../../../types';
import { errorMessage } from '../../../utils/error';
import { validateDialog, getPayloadFromDialog } from './utils';

export type NewDialog = {
	title: string;
	dialogType: 'intent' | 'event';
	dialogValue: string;
	actions: Action;
	responseType: '' | 'flow' | 'message' | 'service';
	recognizes: string;
	messages: string[];
	suggestions: string[];
	isRandom: boolean;
	flowName: string;
	nodeId: string;
	serviceKey: string;
};

export type PartialObj<T> = {
	[P in keyof T]?: any;
};

type Props = {
	dialog?: NewDialog;
};

export type NewMessageAction = Omit<MessageResp, 'responseType'>

// CreateDialog creates a new dialog. Given an exising dialog via props, it's also
// used to edit a dialog.
export function CreateDialog(props: Props) {
	const editorRef = useRef<any>()
	const { dialogId } = useParams();
	const [isOpen, setIsOpen] = useState(false);
	const [actionName, setActionName] = useState('');

	// used to show the message resp for the newly added action
	const [isNewAction, setIsNewAction] = useState(false)

	// used to show the message resp for an existing action
	const [newAction, setNewAction] = useState<NewMessageAction>({
		isRandom: false,
		messages: [],
		suggestions: [],
	})

	const { loading: createLoading, mutate: createMutate } = useMutate(
		api.createDialog,
	);
	const { loading: editLoading, mutate: editMutate } = useMutate(
		api.editDialog,
	);
	const [appId, branchId] = useAppAndBranchId();
	const appVersion = useSelector(
		(state: AppState) => state.apps.appVersion && state.apps.appVersion[appId],
	);
	const versionId = appVersion && appVersion.versionId;
	const { data: flows } = useQuery(() => api.getFlows(appId, versionId));
	const { data: services } = useQuery(() => api.getServices(appId, versionId));
	const history = useHistory();
	const { ErrorToast } = useToast();
	const { chatType } = useSelector((state: RootState) => state.apps.selectedApp)

	const [errors, setErrors] = useState<PartialObj<NewDialog>>({});

	function clearError(key: keyof NewDialog) {
		setErrors((prev) => ({ ...prev, [key]: '' }));
	}

	const [dialog, setDialog] = useState<NewDialog>({
		title: '',
		dialogType: chatType === 'genai' ? 'event' : 'intent',
		dialogValue: '',
		actions: {},
		responseType: '',
		recognizes: '',
		messages: [],
		suggestions: [],
		isRandom: false,
		flowName: '',
		nodeId: '',
		serviceKey: '',
		...(props.dialog ?? {}),
	});

	const flowId =
		flows?.find((item) => item.value === dialog.flowName)?.id ?? '';
	const { data: nodesRes } = useQuery(
		() => api.getNodes(appId, versionId, flowId),
		flowId,
		flowId !== '',
	);

	function updateDialogByKey(key: keyof NewDialog, value: any) {
		setErrors((prev) => ({
			...prev,
			[key]: '',
		}));
		setDialog((prev) => ({
			...prev,
			[key]: value,
		}));
	}

	function updateDialogByKeyValue(values: PartialObj<NewDialog>) {
		setDialog((prev) => ({
			...prev,
			...values,
		}));
	}

	async function handleSave() {
	  const errors: PartialObj<NewDialog> = {}
		if (!validateDialog(errors, dialog)) {
			setErrors(prev => ({...prev, ...errors}))
			return;
		}

		const payload = getPayloadFromDialog(dialog)
		payload.branchId = branchId

		if(editorRef.current) {
			const value: string | undefined = editorRef.current.getValue()
			payload.recognizes = value ? value.trim() : null
		}

		const isEditMode = props.dialog !== undefined;
		if (!isEditMode) {
			const dialogs = await api.getDialogs(appId, branchId);
			const dialogGroup = dialogs.find(
				(item) =>
					item.dialogType === dialog.dialogType &&
					item.dialogValue === dialog.dialogValue,
			);
			if (dialogGroup) {
				payload.order = dialogGroup.dialogGroups.length + 1;
			} else {
				payload.order = 1;
			}
		}

		try {
			if (isEditMode) {
				await editMutate(appId, dialogId, payload);
			} else {
				await createMutate(appId, payload);
			}
			history.push({
				pathname: `/apps/list/${appId}/solo/${chatType === 'genai' ? 'events' : 'qna'}`,
				state: {
					dialog: `${payload.dialogValue}:${payload.dialogType}`
				}
			});
		} catch (err) {
			const op = isEditMode ? 'edit': 'create'
			ErrorToast(errorMessage(op, "dialog", err.code));
		}
	}

	return (
		<WithOverrides
			styles={{ padding: '10px', width: !isOpen ? '458px' : '100%' }}
		>
			<div
				className={style.new}
				style={{
					display: 'grid',
					gridTemplateColumns: `438px 1fr`,
				}}
			>
				<Sidebar
				  editorRef={editorRef}
					errors={errors}
					clearError={clearError}
					isEditMode={props.dialog !== undefined}
					selectedAction={actionName}
					selectAction={(actionName) => setActionName(actionName)}
					services={services ?? []}
					nodes={nodesRes?.nodes ?? []}
					flows={flows ?? []}
					updateDialogByKeyValue={updateDialogByKeyValue}
					updateDialogByKey={updateDialogByKey}
					dialog={dialog}
					viewMessageResp={(visibility) => setIsOpen(visibility)}
					messageRespOpened={isOpen}
					isNewAction={isNewAction}
					setIsNewAction={setIsNewAction}
					newAction={newAction}
					setNewAction={setNewAction}
				/>
				<MessageRespWrapper 
					updateDialogByKey={updateDialogByKey}
				  isNewAction={isNewAction} 
					actionName={actionName} 
					dialog={dialog}
					isOpen={isOpen}
					newAction={newAction}
					setNewAction={setNewAction}
				/>
				<div className={style.cancelSave}>
					<button
						onClick={() => {
							history.push(`/apps/list/${appId}/solo/${chatType === 'genai' ? 'events' : 'qna'}`);
						}}
					>
						Cancel
					</button>
					<button
						onClick={handleSave}
						data-loading={createLoading || editLoading}
					>
						<span>Save</span>
						{(createLoading || editLoading) && (
							<LoadingIcon width="14" height="14" stroke="#FFF" />
						)}
					</button>
				</div>
			</div>
		</WithOverrides>
	);
}

export type SidebarProps = {
	viewMessageResp: (visibility: boolean) => void;
	messageRespOpened: boolean;
	dialog: NewDialog;
	updateDialogByKey: (key: keyof NewDialog, value: any) => void;
	flows: Option[];
	nodes: Option[];
	services: Option[];
	selectAction: (actionName: string) => void;
	selectedAction: string;
	isEditMode: boolean;
	updateDialogByKeyValue: (values: PartialObj<NewDialog>) => void;
	errors: PartialObj<NewDialog>;
	clearError: (key: keyof NewDialog) => void;
	isNewAction: boolean
	setIsNewAction: SetState<boolean>
	newAction: NewMessageAction
	setNewAction: SetState<NewMessageAction>
	editorRef: any
};
